'use client'

import { observeClientOperation } from '@brew/observability/browser'
import type { MetricOptions } from '@brew/observability/metrics'
import type { IntegrationProvider } from '@/lib/integrations/catalog'
import { classifyProductMutationResult } from './product-result'

type IntegrationRequestResult = {
  error?: string
  pendingVerification?: boolean
  warning?: unknown
  revokeAtFigmaUrl?: string
  revokeAtStripeUrl?: string
}

/**
 * Measures the settings acknowledgement, including response decoding. OAuth
 * redirects and pending inbound verification are separate server lifecycles.
 * Credentials, response messages and revocation URLs never enter telemetry.
 */
export function observeIntegrationRequest<T extends IntegrationRequestResult>(
  action: 'setup' | 'disconnect',
  provider: IntegrationProvider,
  context: MetricOptions['context'],
  callback: () => Promise<{
    response: Pick<Response, 'ok' | 'status'>
    result: T
  }>
) {
  return observeClientOperation(
    `integration.${action}.acknowledged`,
    {
      tags: {
        domain: 'integrations',
        surface: 'integration_settings',
        phase: 'request_ack',
        resource: provider,
        transport: 'http',
        trigger: 'user',
      },
      context,
      classifyResult: ({ response, result }) => {
        if (!response.ok) {
          if (response.status === 408 || response.status === 504) {
            return 'timeout'
          }
          return response.status >= 400 && response.status < 500
            ? 'rejected'
            : 'failure'
        }
        if (
          action === 'disconnect' &&
          (result.warning ||
            result.revokeAtFigmaUrl ||
            result.revokeAtStripeUrl)
        ) {
          return 'partial'
        }
        return 'success'
      },
    },
    callback
  )
}

/** Success means the backend accepted the sync, not that contacts were synced. */
export function observeIntegrationSyncAdmission<T>(
  provider: IntegrationProvider,
  trigger: 'start' | 'resume' | 'restart',
  context: MetricOptions['context'],
  callback: () => Promise<T>
) {
  return observeClientOperation(
    'integration.sync.accepted',
    {
      tags: {
        domain: 'integrations',
        surface: 'integration_settings',
        phase: 'admission',
        resource: provider,
        transport: 'server_action',
        trigger,
      },
      context,
      classifyResult: classifyProductMutationResult,
    },
    callback
  )
}
